import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../api/client';
import { Bell, Sun, Moon, Menu, LogOut, User, Store, Search, Sparkles, KeyRound } from 'lucide-react';
import { HealthIndicator } from './HealthIndicator';
import { ChangePasswordModal } from './ChangePasswordModal';
import { useCan } from '../../lib/rbac';
import { useState, useRef, useEffect } from 'react';

export function TopBar() {
  const { t } = useTranslation();
  const { theme, setTheme, language, setLanguage, setSidebarMobileOpen, toggleNotificationPanel, sellerId, setSellerId, sellers, setSellers, aiChatOpen, toggleAiChat } = useUIStore();
  const { user, logout } = useAuthStore();
  const can = useCan();

  // Load sellers on mount
  useEffect(() => {
    api.get<{ sellers: Array<{ id: number; nickname: string; active: boolean; active_sites?: string[] }> }>('/dashboard/connections').then((res: any) => {
      if (res.success && res.data?.sellers) {
        setSellers(res.data.sellers.map((s: any) => ({
          id: s.id,
          nickname: s.nickname,
          active: s.active,
          active_sites: Array.isArray(s.active_sites) ? s.active_sites : [],
        })));
      }
    });
  }, [setSellers]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
    <header className="sticky top-0 z-30">
      {/* ML Brand stripe */}
      <div className="h-[3px] bg-[var(--color-brand)]" />
      <div
        className="bg-[var(--color-surface)] border-b border-[var(--color-border)] flex items-center justify-between px-4"
        style={{ height: 'calc(var(--topbar-height) - 3px)' }}
      >
      {/* Left: mobile menu */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSidebarMobileOpen(true)}
          className="lg:hidden p-2 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Center: search placeholder */}
      <div className="flex-1 max-w-md mx-4 hidden md:block">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder={t('search.placeholder', 'Search orders, ASINs, buyers...')}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-primary)]"
            disabled
          />
        </div>
      </div>

      {/* Right: controls */}
      <div className="flex items-center gap-2">
        {/* Seller selector — la lista ya viene acotada por el servidor a los vendedores propios (GET /dashboard/connections) */}
        {sellers.length > 1 && (
          <div className="flex items-center gap-1.5 mr-1">
            <Store size={14} className="text-[var(--color-text-muted)]" />
            <select
              value={sellerId}
              onChange={(e) => setSellerId(parseInt(e.target.value, 10))}
              className="text-xs bg-transparent border border-[var(--color-border)] rounded px-2 py-1.5 text-[var(--color-text)] cursor-pointer font-medium"
            >
              {sellers.filter(s => s.active).map((s) => (
                <option key={s.id} value={s.id}>{s.nickname}</option>
              ))}
            </select>
          </div>
        )}

        {/* Language — hidden on very small screens */}
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="text-xs bg-transparent border border-[var(--color-border)] rounded px-2 py-1.5 text-[var(--color-text)] cursor-pointer hidden sm:block"
        >
          <option value="es">ES</option>
          <option value="en">EN</option>
        </select>

        {/* AI Assistant — sus respuestas incluyen montos: exige ver dinero. */}
        {can('global.view_money') && (
          <button
            onClick={toggleAiChat}
            className={`p-2 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] ${aiChatOpen ? 'bg-[var(--color-surface-hover)] text-[var(--color-primary)]' : ''}`}
            title="AI Assistant"
          >
            <Sparkles size={18} />
          </button>
        )}

        {/* Theme toggle — hidden on very small screens */}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'default' : 'dark')}
          className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] hidden sm:flex"
          title={theme === 'dark' ? t('settings.themeDefault') : t('settings.themeDark')}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Estado de salud del sistema (incluye Monitor de Precios) */}
        <HealthIndicator />

        {/* Notifications */}
        <button
          onClick={toggleNotificationPanel}
          className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] relative"
        >
          <Bell size={18} />
          <span
            id="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 items-center justify-center px-1"
            style={{ display: 'none' }}
          />
        </button>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--color-surface-hover)]"
          >
            <div className="w-7 h-7 rounded-full bg-[var(--color-primary)] flex items-center justify-center">
              <User size={14} className="text-white" />
            </div>
            <span className="text-sm text-[var(--color-text)] hidden sm:block">
              {user?.display_name || user?.username || 'Admin'}
            </span>
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-[var(--color-border)]">
                <p className="text-sm font-medium text-[var(--color-text)]">{user?.username}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{user?.role_name ?? user?.role}</p>
              </div>
              <button
                onClick={() => { setUserMenuOpen(false); setPasswordModalOpen(true); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              >
                <KeyRound size={16} className="text-[var(--color-text-muted)]" />
                Cambiar contraseña
              </button>
              <button
                onClick={() => { setUserMenuOpen(false); logout(); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[var(--color-error)] hover:bg-[var(--color-surface-hover)]"
              >
                <LogOut size={16} />
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </header>
    {/* Fuera del <header>: la barra es sticky con z-30 y encerraría el diálogo debajo del resto. */}
    <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
    </>
  );
}
